import React, { useState, useEffect } from 'react';
import 'Assets/styles/default/pages/concierge/ManageConciergeSolicitationStyles.less';
import { Col, Row } from 'antd';
import { Table, Typography, VertButton, VertRadio } from '@vertem-core/vertice-ds'
import PropTypes from 'prop-types';
import { DownloadOutlined } from '@ant-design/icons';
import { ActionEnum, CategoryTypeEnum } from 'pages/ManageConciergeSolicitationPage/enum/action-enum';
import { convertPoints, formatToString } from 'Utils/format';
import { useSelector } from 'react-redux';
import { pointsMask } from 'Utils/commons/points';
import { convertNumberToStringCurrency } from 'Utils/convertPoints';
import { shouldConvertToPoints } from 'Constants/Concierge';

const ManageConciergeItemsTable = ({ ...props }) => {

    const {
        solicitation,
        selectedQuotation,
        setSelectedQuotation,
        setListSelectedQuotationId,
        listSelectedQuotationId,
        hasSupplier,
    } = props;

    const isBillPayment = solicitation.redemptionWorkflow?.type == CategoryTypeEnum.BillPayment;

    const {
        pointsName,
        decimalPlaces,
    } = useSelector(state =>
        state.Catalog?.currencyConfiguration
    )

    const [totalItems, setTotalItems] = useState({ value: 0, maskedValue: '' });

    const columnsBaseTable = [
        {
            title: 'Item',
            dataIndex: 'name',
            align: 'center',
            render: (_, { name, disabled }) => (
                name && (
                    <Row>
                        {
                            <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                {name}
                            </Col>
                        }
                    </Row>
                )
            ),
        },
        {
            title: "Valor",
            dataIndex: "valuePoints",
            align: 'center',
            render: (_, { value, valuePoints, disabled }) => (
                <Row>
                    {
                        !value && !valuePoints && (
                            <Col span={24}>
                                -
                            </Col>
                        )
                    }

                    {
                        (!!value || !!valuePoints) && (
                            shouldConvertToPoints
                                ? (
                                    valuePoints &&
                                    <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                        {`${convertPoints(valuePoints, decimalPlaces)} ${pointsName}`}
                                    </Col>
                                )
                                : (
                                    (isBillPayment ? valuePoints : value) &&
                                    <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                        {isBillPayment ? `${convertPoints(valuePoints, decimalPlaces)} ${pointsName}` : convertNumberToStringCurrency(value)}
                                    </Col>
                                )
                        )
                    }
                </Row>
            ),
        },
        {
            title: 'Qtd.',
            dataIndex: 'quantity',
            align: 'center',
            render: (_, { quantity, disabled }) => (
                quantity && (
                    <Row>
                        {
                            <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                {formatToString(quantity)}
                            </Col>
                        }
                    </Row>
                )
            ),
        },
        {
            title: "Total",
            dataIndex: "totalValuePoints",
            align: 'center',
            render: (_, { value, totalValuePoints, quantity, disabled }) => (
                <Row>
                    {
                        !value && !totalValuePoints && (
                            <Col span={24}>
                                -
                            </Col>
                        )
                    }

                    {
                        (!!value || !!totalValuePoints) && (
                            shouldConvertToPoints
                                ? (
                                    totalValuePoints &&
                                    <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                        {`${convertPoints(totalValuePoints, decimalPlaces)} ${pointsName}`}
                                    </Col>
                                )
                                : (
                                    (isBillPayment ? totalValuePoints : value) &&
                                    <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                        {isBillPayment ? `${convertPoints(totalValuePoints, decimalPlaces)} ${pointsName}` : convertNumberToStringCurrency(value * quantity)}
                                    </Col>
                                )
                        )
                    }
                </Row>
            ),
        },
    ];

    const columnsQuotationTable = [
        {
            title: 'Item',
            dataIndex: 'name',
            align: 'center',
            render: (_, { name, disabled }) => (
                name && (
                    <Row>
                        {
                            <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                {name}
                            </Col>
                        }
                    </Row>
                )
            ),
        },
        {
            title: "Valor cotado",
            dataIndex: "valuePointsWithTax",
            align: 'center',
            render: (_, { valuePointsWithTax, disabled }) => (
                valuePointsWithTax && (
                    <Row>
                        {
                            <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                {`${convertPoints(valuePointsWithTax, decimalPlaces)} ${pointsName}`}
                            </Col>
                        }
                    </Row>
                )
            ),
        },
        {
            title: 'Qtd.',
            dataIndex: 'quantity',
            align: 'center',
            render: (_, { quantity, disabled }) => (
                quantity && (
                    <Row>
                        {
                            <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                {formatToString(quantity)}
                            </Col>
                        }
                    </Row>
                )
            ),
        },
        {
            title: "Total cotado",
            dataIndex: "totalValuePointsWithTax",
            align: 'center',
            render: (_, { totalValuePointsWithTax, disabled }) => (
                totalValuePointsWithTax && (
                    <Row>
                        {
                            <Col span={24} className={`${disabled ? 'item-quotation-disabled' : ''}`}>
                                {`${convertPoints(totalValuePointsWithTax, decimalPlaces)} ${pointsName}`}
                            </Col>
                        }
                    </Row>
                )
            ),
        },
    ]

    const renderColumns = () => {
        return solicitation.quotations.length > 0
            ? columnsQuotationTable
            : columnsBaseTable
    }

    const addNonQuotedItems = (quotedItems) => {
        const solicitationItems = [...quotedItems];

        const quotedItemsIds = new Set(quotedItems.map(item => item.id));

        solicitation.solicitationItemsDetails.items.forEach(item => {
            if (!quotedItemsIds.has(item.id)) solicitationItems.push(item);
        });

        return solicitationItems;
    }

    const getQuotationItemsById = (quotationId) => {
        const selectedQuotation = solicitation.quotations.find(quotation => quotation.id === quotationId);
        return selectedQuotation.quotationProductList;
    }

    const hasPossibleApproveAction = () => {
        return solicitation.actions.some(action => action === ActionEnum.Approve || action === ActionEnum.ApproveWithTerms);
    }

    const wrapperItems = () => {
        /*
            Este método retorna uma lista de itens para exibir na tabela, temos as seguintes regras:
                1 - Se a propriedade "approvedQuotation" existir, significa que uma cotação já foi aprovada, com isso o usuário não deve selecionar novamente itens para aprovar, retornamos a cotação aprovada;
                2 - Se existir cotações para exibir e há possibilidade de aprovação, retornamos a tabela permitindo aprovação de itens e seleção de cotações;
                3 - Se existir cotações para exibir e não há possibilidade de aprovação, retornamos a tabela permitindo apenas a seleção de cotações para visualização;
                4 - Se nenhuma das condições forem atingidas, retornamos a tabela com os itens que o usuário informou na criação da solicitação, sem opções.
        */
        if (solicitation.approvedQuotation) {
            return {
                showCheckbox: false,
                supplierLabel: "Fornecedor selecionado:",
                supplier: solicitation.approvedQuotation.supplierName,
                quoteFileName: solicitation.approvedQuotation.file?.fileName,
                quoteFileUrl: solicitation.approvedQuotation.file?.url,
                items: !solicitation.approvedQuotation.quotationProductList ? [] : solicitation.approvedQuotation.quotationProductList.map((item) => ({
                    ...item,
                    disabled: !item.approved
                }))
            };
        }
        if (solicitation.quotations.length > 0 && hasPossibleApproveAction()) {
            var items = selectedQuotation ? getQuotationItemsById(selectedQuotation) : [];

            const solicitationItems = addNonQuotedItems(items);

            return {
                showCheckbox: true,
                items: solicitationItems.map((item) => ({
                    ...item,
                    disabled: !item.quoted
                })),
                quotations: solicitation.quotations
            };
        }

        if (solicitation.quotations.length > 0 && !hasPossibleApproveAction()) {
            const quotationItems = selectedQuotation ? getQuotationItemsById(selectedQuotation) : [];
            const solicitationItems = addNonQuotedItems(quotationItems);

            return {
                quotations: solicitation.quotations,
                showCheckbox: false,
                items: solicitationItems.map(item => ({
                    ...item,
                    disabled: !item.quoted,
                })),
            }
        }

        return {
            showCheckbox: false,
            supplier: solicitation.solicitationItemsDetails.favoriteSupplierName ?? 'Não informado.',
            supplierLabel: "Fornecedor de preferência:",
            items: !solicitation.solicitationItemsDetails.items ? [] : solicitation.solicitationItemsDetails.items.map((item) => ({
                ...item,
                disabled: false,
            }))
        };
    }

    useEffect(() => {
        if (!solicitation?.details?.id) return;

        let items = null;

        if (solicitation.quotations.length > 0)
            items = selectedQuotation && getQuotationItemsById(selectedQuotation).filter(item => item.quoted);

        if (solicitation.approvedQuotation?.quotationProductList)
            items = solicitation.approvedQuotation.quotationProductList.filter(item => item.approved);

        if (!items) {
            if (shouldConvertToPoints || isBillPayment) {
                items = [...solicitation.solicitationItemsDetails.items]
            }
            else {
                const totalValue = solicitation.solicitationItemsDetails.approvedTotalValue;
                const totalValueMasked = convertNumberToStringCurrency(totalValue);
                setTotalItems({
                    value: totalValue,
                    maskedValue: totalValueMasked
                });
                return;
            }
        }

        const totalItemsValue = items.reduce((acc, curr) => acc += curr.totalValuePointsWithTax, 0);
        const totalItemsMasked = `${pointsMask(totalItemsValue, decimalPlaces).value} ${pointsName}`;

        setTotalItems({
            value: totalItemsValue,
            maskedValue: totalItemsMasked
        });

    }, [solicitation]);

    return (

        <>
            {
                !solicitation.approvedQuotation && solicitation.quotations.length > 0 && (
                    <div className='margin-top-small'>
                        <Col className='margin-bottom-small'>
                            <Typography.Text className='details-card-title'>
                                Cotações
                            </Typography.Text>
                        </Col>

                        <Row gutter={[16, 16]}>
                            {
                                wrapperItems().quotations.map(quotation =>
                                    <Col span={24} className='container-radio-supplier'>
                                        <div
                                            className='container-radio-supplier'
                                            onClick={() => {
                                                setSelectedQuotation(quotation.id);
                                                setListSelectedQuotationId([]);
                                            }}
                                        >
                                            <VertRadio
                                                key={quotation.id}
                                                checked={selectedQuotation == quotation.id}
                                            />
                                            {quotation.supplierName}
                                        </div>

                                        <VertButton
                                            className='container-quotation-file'
                                            type="default"
                                            shape="round"
                                            icon={<DownloadOutlined />}
                                            size="small"
                                            download
                                            href={quotation.file.url}
                                        >
                                            {quotation.file.fileName}
                                        </VertButton>
                                    </Col>
                                )
                            }
                        </Row>
                    </div>
                )
            }

            {
               hasSupplier && wrapperItems().supplier &&
                <div className='margin-top-small'>
                    <Typography.Text className='details-card-title manage-solicitation-supplier' >
                        {wrapperItems().supplierLabel}
                    </Typography.Text>
                    <Typography.Text>
                        {wrapperItems().supplier}
                    </Typography.Text>

                    {
                        wrapperItems().quoteFileName &&
                            <VertButton
                                className='container-quotation-file'
                                type="default"
                                shape="round"
                                icon={<DownloadOutlined />}
                                size="small"
                                download
                                href={wrapperItems().quoteFileUrl}
                            >
                                {wrapperItems().quoteFileName}
                            </VertButton>
                    }
                </div>
            }

            {
                wrapperItems().items.length > 0 &&
                <>
                    <Table
                        bordered
                        className='margin-top-small quotation-table'
                        pagination={false}
                        rowSelection={
                            wrapperItems().showCheckbox ?
                                {
                                    type: 'checkbox',
                                    selectedRowKeys: listSelectedQuotationId,
                                    onChange: (selected) => {
                                        setListSelectedQuotationId(selected);
                                    },
                                    getCheckboxProps: (item) => ({
                                        disabled: item.disabled
                                    })
                                } : null}
                        dataSource={wrapperItems().items}
                        columns={renderColumns()}
                        scroll={{ x: "800px" }}
                    />

                    <Row className="manage-concierge-table-bottom-infos" justify={"space-between"}>
                        <p className="table-caption">
                            • <span>Produto/serviço exemplo</span>: Item não cotado/aprovado.
                        </p>

                        {totalItems.value > 0 &&
                            <p className="solicitation-total-value">
                                <span>Total:</span> {totalItems.maskedValue}
                            </p>
                        }
                    </Row>
                </>
            }
        </>
    )
}

ManageConciergeItemsTable.propTypes = {
    solicitation: PropTypes.object.isRequired,
    selectedQuotation: PropTypes.object.isRequired,
    listSelectedQuotationId: PropTypes.object.isRequired,
    setSelectedQuotation: PropTypes.func.isRequired,
    setListSelectedQuotationId: PropTypes.func.isRequired,
    hasSupplier: PropTypes.bool.isRequired,
};

export default ManageConciergeItemsTable;